/**
 * Accordion
 * @class
 * @constructor
 * @public
 */
class Accordion {
  /**
   * Constructor
   * @param {string} name The container to handle
   * @returns {object} options The options
   * @returns {class}
   */
  constructor() {
    this.accordions = document.getElementsByClassName('accordion--js');

    // Engage engines
    if (this.accordions) {
      this.init();
    }
  }

  toggleAccordionItem() {
    if (!document.location.hash) return;

    const hashValue = decodeURIComponent(document.location.hash.substr(1)) || null;
    if (!hashValue) return;

    const target = document.getElementById(hashValue);
    if (!target) return;

    // Direct match: the accordion-item itself carries the ID.
    // Fallback: the ID sits somewhere inside an accordion item (e.g. old deep links)
    const accordionItem = target.classList.contains('accordion-item')
      ? target
      : target.closest('.accordion-item');
    if (!accordionItem) return;

    // Items beyond the initially visible ones are display:none until the accordion
    // is expanded, so opening the trigger alone would have no visible effect and the
    // browser's own scroll to the hash target has already failed.
    if (accordionItem.classList.contains('accordion-item--is-hidden')) {
      const accordion = accordionItem.closest('.accordion--js');
      if (accordion) {
        this.setExpanded(accordion, true);
        accordionItem.scrollIntoView();
      }
    }

    const trigger = accordionItem.querySelector('.accordion-item__trigger');
    if (trigger) trigger.click();
  }

  /**
   * Expand or collapse the "show all entries" state of a single accordion
   * @param {HTMLElement} accordion The accordion section
   * @param {boolean} expand Whether the hidden items should be revealed
   * @returns {void}
   */
  setExpanded(accordion, expand) {
    const button = accordion.getElementsByClassName('button-expand--js')[0];
    if (!button) return;
    if (accordion.classList.contains('accordion--is-expanded') === expand) return;

    // Collapsing while the focus sits inside an item that is about to be hidden
    // would drop the focus to <body> - move it to the button beforehand.
    if (!expand) {
      const active = document.activeElement;
      const hiddenItem = active && active.closest && active.closest('.accordion-item--is-hidden');
      if (hiddenItem && accordion.contains(hiddenItem)) button.focus();
    }

    accordion.classList.toggle('accordion--is-expanded', expand);
    button.setAttribute('aria-expanded', expand ? 'true' : 'false');

    const label = expand ? button.dataset.labelCollapse : button.dataset.labelExpand;
    if (label) button.textContent = label;

    const status = accordion.getElementsByClassName('accordion__expand-status')[0];
    if (status) {
      status.textContent = expand
        ? (button.dataset.statusExpanded || '')
        : (button.dataset.statusCollapsed || '');
    }
  }

  /**
   * Initialize the component
   * @returns {void}
   */
  init() {
    Array.prototype.forEach.call(this.accordions, (accordion) => {
      // Expand button
      const expandButton = accordion.getElementsByClassName('button-expand--js')[0];
      if (expandButton) {
        expandButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.setExpanded(accordion, !accordion.classList.contains('accordion--is-expanded'));
        });
      }

      // Set initial inert state on all closed bodies
      const bodies = accordion.getElementsByClassName('accordion-item__body');
      Array.prototype.forEach.call(bodies, (body) => {
        body.setAttribute('inert', '');
      });

      // Accordion trigger buttons
      const triggers = accordion.getElementsByClassName('accordion-item__trigger');
      Array.prototype.forEach.call(triggers, (trigger) => {
        trigger.addEventListener('click', () => {
          const accordionItem = trigger.closest('.accordion-item');
          const body = accordionItem.querySelector('.accordion-item__body');
          const isOpen = accordionItem.classList.contains('is-open');

          if (!body) return;

          // Cancel any pending rAF from a previous close sequence
          if (body._pendingRaf) {
            window.cancelAnimationFrame(body._pendingRaf);
            body._pendingRaf = null;
          }
          // Remove any stale transitionend listener before registering a new one
          if (body._transitionHandler) {
            body.removeEventListener('transitionend', body._transitionHandler);
            body._transitionHandler = null;
          }

          if (isOpen) {
            // Closing: pin to a numeric pixel value before animating to 0.
            // If maxHeight is 'none' (fully open), use scrollHeight.
            // If mid-animation, use the current computed value to avoid a visual snap.
            body.style.maxHeight = body.style.maxHeight === 'none'
              ? body.scrollHeight + 'px'
              : window.getComputedStyle(body).maxHeight;
            const raf1 = window.requestAnimationFrame(() => {
              body._pendingRaf = window.requestAnimationFrame(() => {
                body._pendingRaf = null;
                body.style.maxHeight = '0';
              });
            });
            body._pendingRaf = raf1;

            body._transitionHandler = () => {
              body.removeEventListener('transitionend', body._transitionHandler);
              body._transitionHandler = null;
              body.setAttribute('inert', '');
            };
            body.addEventListener('transitionend', body._transitionHandler);

            accordionItem.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            // Opening: measure full intrinsic height and animate to it
            body.removeAttribute('inert');
            body.style.maxHeight = body.scrollHeight + 'px';

            body._transitionHandler = () => {
              body.removeEventListener('transitionend', body._transitionHandler);
              body._transitionHandler = null;
              // Remove inline max-height so content can reflow freely (e.g. images loading)
              body.style.maxHeight = 'none';
            };
            body.addEventListener('transitionend', body._transitionHandler);

            accordionItem.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });

    this.toggleAccordionItem();

    return this;
  }

  /**
   * Class name
   * @returns {string} The human readable class name
   */
	get name() {
    return 'Accordion';
	}
};

export default Accordion;
